import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAffAuth } from "../store";
import { formatApiError } from "../api";
import { useT, useAffLangInit } from "../i18n";
import LangPicker from "../LangPicker";

export default function AffSignup() {
  const { signup } = useAffAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("token") || "";
  const t = useT();
  useAffLangInit();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    invite_token: inviteToken,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, invite_token: inviteToken }));
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
      };
      if (form.invite_token) payload.invite_token = form.invite_token;
      await signup(payload);
      navigate("/affiliate", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="dark fixed inset-0 flex items-center justify-center bg-black overflow-hidden"
      data-testid="aff-signup-screen"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-amber-500/[0.06] blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/[0.05] blur-3xl" />
      </div>
      <form
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm bg-black/60 border border-white/10 backdrop-blur-xl p-8 space-y-5"
        data-testid="aff-signup-form"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-3xl font-medium text-white tracking-tight">
              Clipra
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/40 mt-1">
              {inviteToken ? t("signup.subtitle.claim") : t("signup.subtitle.apply")}
            </div>
          </div>
          <LangPicker compact />
        </div>

        {inviteToken && (
          <div
            className="text-xs text-emerald-400 border-l-2 border-emerald-500 pl-3 py-1 font-mono"
            data-testid="aff-signup-invite-hint"
          >
            {t("signup.invite_hint")}
          </div>
        )}

        <div className="space-y-3">
          <Field
            label={t("signup.name")}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            placeholder={t("signup.placeholder.name")}
            testid="aff-signup-name"
          />
          <Field
            label={t("common.email")}
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
            placeholder="you@example.com"
            testid="aff-signup-email"
          />
          <Field
            label={t("common.password")}
            type="password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            required
            placeholder={t("signup.placeholder.password")}
            testid="aff-signup-password"
          />
        </div>

        {error && (
          <div
            className="text-sm text-red-400 border-l-2 border-red-500 pl-3 py-1 font-mono"
            data-testid="aff-signup-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-white text-black py-2.5 text-sm font-medium tracking-wide hover:bg-white/90 active:translate-y-px disabled:opacity-50"
          data-testid="aff-signup-submit"
        >
          {busy
            ? t("signup.busy.creating")
            : inviteToken
              ? t("signup.cta.claim")
              : t("signup.cta.create")}
        </button>

        <div className="text-center text-xs font-mono text-white/40">
          {t("signup.already")}{" "}
          <Link
            to="/affiliate/login"
            className="text-emerald-400 hover:text-emerald-300"
            data-testid="aff-signup-to-login"
          >
            {t("common.sign_in")}
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
      />
    </div>
  );
}
